/* eslint-disable no-undef */
import _ from 'lodash';
import angular from 'angular';
import logger from '../../../shared/logger';
import UtilsService from '../../../services/utilsService';
import { EmployerRepAuthTypes } from '../../../constants/employerRepAuthTypes';
import Logger from '../../../shared/logger';

export const authTypes = {
    LINKEDIN: 'linkedin',
};

export const authErrorCodes = {
    TOO_MANY_ATTEMPTS: 'too_many_attempts',
    INVALID_GRANT: 'invalid_grant',
};

export const authEntityTypes = {
    EMPLOYER_REP: 'employerRep',
    RECRUITER: 'recruiter',
};

export const AuthService = (
    $rootScope,
    $state,
    $cookies,
    angularAuth0,
    Restangular,
    $localStorage,
    $sessionStorage,
    $translate,
    $q,
    hotjar,
    RouteService,
    RecruiterService,
    searchCacheService,
    $timeout,
    EmployerRepService,
    EmployerService,
    NotificationService,
    EnvSettings,
    permissions,
) => {
    let isLoggingOut = false;
    const clearSession = function () {
        Restangular.setDefaultHeaders({});
        const sysPrefs = $localStorage.sysprefs;
        const { sysDefaults } = $localStorage;
        const { nextState } = $localStorage;
        const employerRepReturnUrlParams = angular.copy($localStorage.employerRepReturnUrlParams);
        $localStorage.$reset();
        localStorage.clear();
        $localStorage.UILanguage = null;
        $localStorage.sysprefs = sysPrefs;
        $localStorage.sysDefaults = sysDefaults;
        $localStorage.nextState = nextState;
        $localStorage.employerRepReturnUrlParams = employerRepReturnUrlParams;

        const options = { domain: window.location.hostname.split('.').slice(-2).join('.') };

        $cookies.remove('authResult', options);
        $cookies.remove('origUserPrefs', options);
        $cookies.remove('userprefs', options);
        $cookies.remove('userData', options);
        $cookies.remove('authData', options);
    };

    const checkIsRecruiterUser = (user) => {
        const userRoles = _.get(user, 'userData.roles', []);
        const entityId = _.get(user, 'userPrefs.entityId', null);

        return userRoles.includes('TE') && entityId;
    };

    const checkIsEmployerRepUser = (user) => {
        const userRoles = _.get(user, 'userData.roles', []);
        const employerRepId = _.get(user, 'origUserPrefs.employerRepId', null);

        return userRoles.includes('EM') && employerRepId;
    };

    const getAuthEntityType = () => {
        UtilsService.cookiesOverride($cookies);
        const origUserPrefs = UtilsService.parseJSON($cookies.get('origUserPrefs'));
        const entityId = _.get(origUserPrefs, 'data.entityId', null);
        const employerRepId = _.get(origUserPrefs, 'employerRepId', null);

        if (entityId) {
            return authEntityTypes.RECRUITER;
        }

        if (employerRepId) {
            return authEntityTypes.EMPLOYER_REP;
        }
    };

    async function initUserTracking({ user, loginType, currentUserData = {} }) {
        try {
            const userRoles = _.get(user, 'userData.roles', []);
            const userData = _.get(user, 'userData', {});
            const _id = _.get(user, 'origUserPrefs._id', {});

            if (checkIsRecruiterUser(user)) {
                const {
                    _id: recruiterId,
                    created,
                    fullName,
                } = currentUserData;

                const currentUser = {
                    ...userData,
                    _id,
                    recruiterId,
                    created,
                    fullName,
                    userRoles,
                    roles: userRoles.join(),
                    name: fullName || getUserName(userData, loginType === 'social'),
                    email: userData.id || userData.email,
                    loginType: loginType === 'social' ? 'Linkedin' : 'Talenya',
                };

                localStorage.setItem('trackUserData', JSON.stringify(currentUser));
            } else {
                const {
                    employerId,
                    _id: employerRepId,
                    created,
                    name: fullName,
                    roles,
                } = currentUserData;
                const { result: employer } = await EmployerService.getEmployer(employerId);
                const { name: employerName, office } = employer;

                const currentUser = {
                    ...userData,
                    _id,
                    employerId,
                    employerRepId,
                    created,
                    employerName,
                    fullName,
                    userRoles,
                    roles: roles.join(),
                    office,
                    name: fullName || getUserName(userData, loginType === 'social'),
                    email: userData.id || userData.email,
                    loginType: loginType === 'social' ? 'Linkedin' : 'Talenya',
                };

                localStorage.setItem('trackUserData', JSON.stringify(currentUser));
            }

            hotjar.identifyUser();
        } catch (err) {
            logger.error(err);
        }

        function getUserName(currentUser, isLinkedin) {
            return isLinkedin
                ? currentUser.name
                : (currentUser.fullName || currentUser.email || currentUser.id);
        }
    }

    const getCurrentUserData = async (user) => {
        try {
            if (checkIsRecruiterUser(user)) {
                const entityId = _.get(user, 'userPrefs.entityId', null);
                const { result: recruiter } = await RecruiterService.getRecruiter(entityId);

                return recruiter;
            }

            if (checkIsEmployerRepUser(user)) {
                const employerRepId = _.get(user, 'origUserPrefs.employerRepId', null);
                const { result: employerRep } = await EmployerRepService.getExistingEmployerRep(employerRepId);

                return employerRep;
            }

            return null;
        } catch (err) {
            logger.error(err);
            return null;
        }
    };

    async function createSession({
        user, moveOn, loginType, isSignUpEvent,
    }) {
        const isRemoteHost = !(window.location.hostname.indexOf('localhost') > -1);

        const expiresInSec = 60 * 60 * 24;
        const authTokenExpiresOnDate = moment().add(expiresInSec, 's').toDate();

        const options = {
            domain: window.location.hostname.split('.').slice(-2).join('.'), // set only domain zone and domain 1 level
            secure: isRemoteHost,
            samesite: 'strict',
            expires: authTokenExpiresOnDate,
        };

        UtilsService.cookiesOverride($cookies);

        $cookies.put('origUserPrefs', JSON.stringify(user?.origUserPrefs), options);
        $cookies.put('userprefs', JSON.stringify(user?.userPrefs), options);
        $cookies.put('userData', JSON.stringify(user?.userData), options);

        function handleLogout() {
            $sessionStorage.singup = null;
            RouteService.logout();
            Restangular.setDefaultHeaders({});
        }

        const currentUserData = await getCurrentUserData(user);
        const userStatus = _.get(currentUserData, 'status.current');

        if (!currentUserData) {
            NotificationService.error('REMOVED_USER_LOGIN_TO_SYSTEM');
            logger.error(`Login failed for ${user.userData.name}: user was already deleted`);
            handleLogout();
            return;
        }

        if (userStatus === 'inactive') {
            NotificationService.error('UNAUTHORIZED_TO_ACCESS_EMPLOYER_REP');
            logger.error(`Login failed for user ${user.userData.name} with status inactive`);
            handleLogout();
            return;
        }

        try {
            await initUserTracking({ user, loginType, currentUserData });
        } catch (err) {
            logger.error(`Error on init user tracking: ${err}`);
        }

        $translate.refresh('en-US');
        if (moveOn) {
            return;
        }
        // TODO roles
        const userPrefs = UtilsService.parseJSON($cookies.get('userprefs'));
        const userData = UtilsService.parseJSON($cookies.get('userData'));

        const noRoles = _.get(userData, 'roles', []).length === 0;
        if (noRoles) {
            let userMessage = 'User tried to login';
            if ($sessionStorage.userType === 'employer') {
                userMessage = 'User tried to login as employer';
            } else if ($sessionStorage.userType === 'recruiter') {
                userMessage = 'User tried to login as recruiter';
            }
            RouteService.addUserData({
                user_metadata: {
                    status: userMessage,
                },
            })
                .catch((err) => logger.error(err));

            if ($sessionStorage.singup === 'employer') {
                $sessionStorage.singup = null;
                $state.go('addEmployer');
                return;
            } if ($sessionStorage.singup === 'recruiter') {
                $sessionStorage.singup = null;
                $state.go('addTalentExpert');
                return;
            }

            $sessionStorage.singup = null;
            RouteService.logout();
            Restangular.setDefaultHeaders({});
            $cookies.remove('authResult', { domain: window.location.hostname.split('.').slice(-2).join('.') });

            const userType = _.get(userData, 'user_signup');

            if (userPrefs) {
                // user Record does exist
                if (userPrefs?.entityId) {
                    // entity Id does exist
                    return $state.go('register');
                }

                NotificationService.error('UNAUTHORIZED_TO_LOGIN_TO_SYSTEM');
                $state.go('logout');
            } else if (userType === 'employer') {
                return $state.go('addEmployer');
            } else if (userType === 'talentExpert') {
                return $state.go('addTalentExpert');
            }
        } else if (!userPrefs && !$localStorage.employerRepReturnUrlParams) {
            // user record does not exist and roles does exist.
            RouteService.sendMail({
                from: 'info',
                body: `User with Roles has no user record in DB user email : ${userData.email}`,
                to: ['info@talenya.com'],
                subject: 'Error in Login',
                firstName: 'Talenya',
                talentExpertName: 'Talenya',
            })
                .catch((err) => logger.error(err));
            NotificationService.error('UNAUTHORIZED_TO_LOGIN_TO_SYSTEM');
            return $state.go('logout');
        }
        $sessionStorage.singup = null;
        $sessionStorage.userType = null;
        searchCacheService.init();
        if ($localStorage.nextState) {
            const { nextState } = $localStorage;
            $localStorage.nextState = null;
            $state.go(nextState.state, nextState.params);
            return;
        }

        /* $localStorage.userData.roles = ['TE'];
        //TODO redirect according to role; */
        window.location.hash = '#/default';
        return false;
    }

    const _login = function (credentials, state) {
        return new Promise((resolve, reject) => {
            angularAuth0.client.login({
                realm: 'Username-Password-Authentication',
                username: credentials.username.toLowerCase(),
                password: credentials.password,
                state,
            }, (error, result) => {
                if (error) {
                    const authenticationFailedError = new Error(`Authentication failed for username: ${credentials.username}`);
                    logger.warn(authenticationFailedError);
                    resolve({ error, authResponse: null });
                    return;
                }

                resolve({ error: null, authResponse: result });
            });
        });
    };

    const login = async function (credentials, state) {
        try {
            clearSession();

            let authResult = null;
            let { authResponse, error } = await _login(credentials, state);
            if (error) {
                const isUserBlockedByAuthError = error.code === authErrorCodes.TOO_MANY_ATTEMPTS;
                const isWrongCredentialsError = error.code === authErrorCodes.INVALID_GRANT;

                if (isUserBlockedByAuthError) {
                    const { result: { isActivated, minutesToWait } } = await RouteService.unBlockUser({ email: credentials.username });

                    if (isActivated) {
                        const response = await _login(credentials, state);
                        authResponse = response.authResponse;
                        error = response.error;

                        if (error) {
                            return { error: 'LOGIN_WRONG_CREDENTIALS_ERROR_MESSAGE' };
                        }
                    } else {
                        return {
                            error: `Your account is temporary blocked due to multiple failed login attempts. Please try to log in again in ${minutesToWait} minutes.`,
                            errorCode: error.code,
                        };
                    }
                }

                if (isWrongCredentialsError) {
                    return { error: 'LOGIN_WRONG_CREDENTIALS_ERROR_MESSAGE' };
                }
            }

            authResult = authResponse;

            if (state) {
                authResult.state = state;
            }

            if (authResult.idToken) {
                await setUser(authResult, 'regular');
                return { error: false, authResult };
            }
        } catch (error) {
            return { error: 'LOGIN_WRONG_CREDENTIALS_ERROR_MESSAGE' };
        }
    };

    const loginWith = function (loginWithApp, state) {
        clearSession();

        angularAuth0.authorize({
            scope: 'openid email',
            connection: loginWithApp,
            state,
        });
    };

    const changePassword = function (email) {
        return new Promise((resolve, reject) => {
            angularAuth0.changePassword({
                connection: 'Username-Password-Authentication',
                email,
            }, (error, message) => {
                if (error) {
                    reject(error);

                    return;
                }

                resolve(message);
            });
        });
    };

    async function setUser(authResult, type) {
        const expiresInSec = _.get(authResult, 'expiresIn', 60 * 60 * 24);
        const authTokenExpiresOnDate = moment().add(expiresInSec, 's').toDate();

        const isRemoteHost = !(window.location.hostname.indexOf('localhost') > -1);

        UtilsService.cookiesOverride($cookies);

        $cookies.put('authResult', 'ok', {
            domain: window.location.hostname.split('.').slice(-2).join('.'), // set only domain zone and domain 1 level
            secure: isRemoteHost,
            samesite: 'strict',
            expires: authTokenExpiresOnDate,
        });

        try {
            await RouteService.login({
                token: authResult.idToken,
                expiresInSec,
            });

            let [{ result: data }] = await Promise.all([
                RouteService.getUser(),
                RouteService.init(),
            ]);

            let moveOn = false;
            let isSignUpEvent = false;
            if (authResult.state && typeof authResult.state === 'string') {
                const splitBySlash = authResult.state.split('/');

                const lastSlug = splitBySlash[splitBySlash.length - 1];
                if (lastSlug === 'activate') {
                    isSignUpEvent = true;
                    data = { ...data, userData: { ...data.userData, roles: ['EM'] } };
                    $state.go('employerRep.signUp.activate', { employerRepId: splitBySlash[1] });
                    moveOn = true;
                }
                if (lastSlug === 'activate-linkedin') {
                    isSignUpEvent = true;
                    $state.go('employerRep.signUp.activateLinkedin', { employerRepId: splitBySlash[1] });
                    moveOn = true;
                }
            }

            createSession({
                user: data, moveOn, loginType: type, isSignUpEvent,
            });
        } catch (error) {
            logger.error(error);
            $state.go('logout');
        }
    }

    const handleParseHash = function () {
        angularAuth0.parseHash((err, authResult) => {
            if (authResult && authResult.idToken) {
                setUser(authResult, 'social');
            } else if (err) {
                logger.warn(err);
            }
        });
    };

    const isAuthenticated = function () {
        UtilsService.cookiesOverride($cookies);
        const isAuthenticated = $cookies.get('authResult');
        return isAuthenticated;
    };

    const logout = async (deep) => {
        if (isLoggingOut) {
            return;
        }
        isLoggingOut = true;
        const currUrl = window.location.href;
        UtilsService.cookiesOverride($cookies);
        let redirectUrl;
        let isSso = false;
        let employerId = '';
        if (!permissions.isTalenyaUser() && !permissions.isTalentExpertUser()) {
            const origin = `${EnvSettings.WebAppUrl}`;
            let authData = UtilsService.parseJSON(decodeURIComponent($cookies.get('authData')));
            redirectUrl = `${origin}/loginsso?redirectUrl=${encodeURIComponent(currUrl)}${deep ? '&needLogout=true' : ''}`;
            if (authData?.employerId && authData?.authType === EmployerRepAuthTypes.SSO && authData?.data) {
                isSso = true;
            }

            const urlData = currUrl.split('/');
            const employerIndex = _.findIndex(urlData, (i) => i === 'employer');
            if (employerIndex >= 0 && employerIndex < urlData.length - 1 && urlData[employerIndex + 1].length === 24) {
                employerId = urlData[employerIndex + 1];
            }

            if (employerId) {
                const res = await Restangular.all('auth')
                    .all('data')
                    .post({ employerId })
                    .catch(err => Logger.error(error));

                authData = res?.result?.authData || {};
                authData.employerId = res?.result?.employerId;
                if (authData?.employerId && authData?.authType === EmployerRepAuthTypes.SSO && authData?.data) {
                    isSso = true;
                }
            }
        }

        let token;
        if (isSso) {
            const tokenResult = await RouteService.getIdToken();
            token = tokenResult?.result;
        }
        await RouteService.logout();
        await RouteService.delay(100);
        clearSession();
        if (isSso && redirectUrl) {
            if (token && employerId && !deep) {
                try {
                    await setUser({ idToken: token }, 'regular');
                    window.location.reload();
                } catch (e) {
                    await RouteService.logout();
                    clearSession();
                    window.location.href = redirectUrl;
                }
            } else {
                window.location.href = redirectUrl;
            }
            return;
        }

        isLoggingOut = false;

        const isOnlyWebAppLoginAllowed = await RouteService.getSystemPreferenceAsync('isOnlyWebAppLoginAllowed') !== false;
        if (isOnlyWebAppLoginAllowed) {
            const origin = `${EnvSettings.WebAppUrl}`;
            window.location.href = origin;
        } else {
            $state.go('login');
        }
    };

    const getEmployerRepIdStateAsync = async ({
        targetEmployerRepId,
        currentUserId,
    }) => EmployerRepService.getAuthenticationActionForEmployerFeedback({ targetEmployerRepId, currentUserId });

    const getCurrentUserEntityId = () => {
        UtilsService.cookiesOverride($cookies);
        const origUserPrefs = UtilsService.parseJSON($cookies.get('origUserPrefs'));
        const userPrefs = UtilsService.parseJSON($cookies.get('userprefs'));

        const employerRepId = _.get(origUserPrefs, 'employerRepId');
        const entityId = _.get(userPrefs, 'entityId');
        return entityId || employerRepId;
    };

    const getTrackUserData = () => {
        let user = {};
        try {
            user = JSON.parse(localStorage.getItem('trackUserData'));
        } catch (err) {
            logger.warn(err);
        }

        return user;
    };

    const goToSsoLoginPage = (host, employerId, email, isOnlyFastRatingAllowed, successUrl) => {
        let redirectUri = `${host}/employer/${employerId}/positions`;
        if (successUrl) {
            redirectUri = decodeURI(successUrl);
        } else if (isOnlyFastRatingAllowed) {
            const employerPart = `employer/${employerId}/`;
            redirectUri = `${host}/${employerPart}successful-activation-page`;
        }
        const extraParams = `&needLogout=true&loginHint=${email}&connection=Username-Password-Authentication&employerId=${employerId}`;
        const url = `${host}/loginsso?redirectUrl=${redirectUri}${extraParams}`;
        window.location.href = url;
    };

    return {
        login,
        logout,
        isAuthenticated,
        handleParseHash,
        loginWith,
        clearSession,
        changePassword,
        getEmployerRepIdStateAsync,
        getCurrentUserEntityId,
        getTrackUserData,
        getAuthEntityType,
        setUser,
        goToSsoLoginPage,
    };
};

angular
    .module('TALENYA-FE')
    .factory('AuthService', AuthService);
